import * as Y from "yjs";
import type { Annotation, AnnotationReply, UserInfo } from "../types";

/**
 * Get or create the shared annotations Y.Map from a Yjs doc.
 * Each annotation is stored as a Y.Map keyed by annotation ID.
 */
export function getAnnotationsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap("annotations") as Y.Map<Y.Map<unknown>>;
}

/**
 * Generate a unique annotation ID
 */
function generateId(): string {
  return `ann_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Convert a Y.Map annotation entry to a plain Annotation object
 */
function yMapToAnnotation(yMap: Y.Map<unknown>): Annotation {
  const repliesArray = yMap.get("replies") as
    | Y.Array<Y.Map<unknown>>
    | undefined;
  const replies: AnnotationReply[] = [];

  if (repliesArray) {
    repliesArray.forEach((replyMap: Y.Map<unknown>) => {
      replies.push({
        id: replyMap.get("id") as string,
        text: replyMap.get("text") as string,
        author: replyMap.get("author") as UserInfo,
        createdAt: replyMap.get("createdAt") as string,
      });
    });
  }

  return {
    id: yMap.get("id") as string,
    frameId: yMap.get("frameId") as string,
    x: yMap.get("x") as number,
    y: yMap.get("y") as number,
    text: yMap.get("text") as string,
    author: yMap.get("author") as UserInfo,
    createdAt: yMap.get("createdAt") as string,
    resolved: yMap.get("resolved") as boolean,
    replies,
  };
}

/**
 * Read all annotations from the Yjs document as plain objects
 */
export function readAnnotations(doc: Y.Doc): Annotation[] {
  const map = getAnnotationsMap(doc);
  const annotations: Annotation[] = [];

  map.forEach((yMap) => {
    annotations.push(yMapToAnnotation(yMap));
  });

  // Sort by creation time
  annotations.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return annotations;
}

/**
 * Add a new annotation to the Yjs document
 */
export function addAnnotation(
  doc: Y.Doc,
  frameId: string,
  position: { x: number; y: number },
  text: string,
  author: UserInfo,
): Annotation {
  const map = getAnnotationsMap(doc);
  const id = generateId();

  const annotation: Annotation = {
    id,
    frameId,
    x: position.x,
    y: position.y,
    text,
    author,
    createdAt: new Date().toISOString(),
    resolved: false,
    replies: [],
  };

  const yMap = new Y.Map<unknown>();
  yMap.set("id", annotation.id);
  yMap.set("frameId", annotation.frameId);
  yMap.set("x", annotation.x);
  yMap.set("y", annotation.y);
  yMap.set("text", annotation.text);
  yMap.set("author", annotation.author);
  yMap.set("createdAt", annotation.createdAt);
  yMap.set("resolved", annotation.resolved);
  yMap.set("replies", new Y.Array<Y.Map<unknown>>());

  map.set(id, yMap);

  return annotation;
}

/**
 * Add a reply to an existing annotation thread
 */
export function addReply(
  doc: Y.Doc,
  annotationId: string,
  text: string,
  author: UserInfo,
): AnnotationReply | null {
  const map = getAnnotationsMap(doc);
  const yMap = map.get(annotationId);
  if (!yMap) return null;

  const reply: AnnotationReply = {
    id: `reply_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    text,
    author,
    createdAt: new Date().toISOString(),
  };

  const repliesArray = yMap.get("replies") as Y.Array<Y.Map<unknown>>;
  const replyMap = new Y.Map<unknown>();
  replyMap.set("id", reply.id);
  replyMap.set("text", reply.text);
  replyMap.set("author", reply.author);
  replyMap.set("createdAt", reply.createdAt);

  repliesArray.push([replyMap]);

  return reply;
}

/**
 * Resolve or unresolve an annotation
 */
export function setAnnotationResolved(
  doc: Y.Doc,
  annotationId: string,
  resolved: boolean,
): void {
  const map = getAnnotationsMap(doc);
  const yMap = map.get(annotationId);
  if (yMap) {
    yMap.set("resolved", resolved);
  }
}

/**
 * Delete an annotation from the Yjs document
 */
export function deleteAnnotation(doc: Y.Doc, annotationId: string): void {
  const map = getAnnotationsMap(doc);
  map.delete(annotationId);
}

/**
 * Observe changes to annotations and call the handler with the full list
 */
export function observeAnnotations(
  doc: Y.Doc,
  handler: (annotations: Annotation[]) => void,
): () => void {
  const map = getAnnotationsMap(doc);

  const onUpdate = () => {
    handler(readAnnotations(doc));
  };

  // Deep observe covers changes to individual annotation maps and their reply arrays
  map.observeDeep(onUpdate);

  return () => {
    map.unobserveDeep(onUpdate);
  };
}
